import { drawdown, mulberry32 } from './risk'
import { quantile } from './statistics'

export interface BootstrapSummary {
  samples: number
  observedReturnPct: number
  p5ReturnPct: number
  medianReturnPct: number
  p95ReturnPct: number
  pProfit: number
  distribution: number[]
}

function compoundPct(returns: readonly number[]): number {
  let growth = 1
  for (const r of returns) growth *= 1 + r
  return (growth - 1) * 100
}

// Block bootstrap preserves short-run autocorrelation that a plain i.i.d. resample would destroy.
export function bootstrapReturns(
  stepReturns: readonly number[],
  opts: { samples?: number; seed?: number; blockSize?: number } = {},
): BootstrapSummary {
  const samples = opts.samples ?? 1000
  const blockSize = Math.max(1, opts.blockSize ?? 5)
  const rng = mulberry32(opts.seed ?? 11)
  const n = stepReturns.length
  const observedReturnPct = compoundPct(stepReturns)

  if (n === 0) {
    return { samples: 0, observedReturnPct: 0, p5ReturnPct: 0, medianReturnPct: 0, p95ReturnPct: 0, pProfit: 0, distribution: [] }
  }

  const results: number[] = []
  for (let s = 0; s < samples; s++) {
    const resampled: number[] = []
    while (resampled.length < n) {
      const start = Math.floor(rng() * n)
      for (let b = 0; b < blockSize && resampled.length < n; b++) resampled.push(stepReturns[(start + b) % n])
    }
    results.push(compoundPct(resampled))
  }

  const profit = results.filter((r) => r > 0).length
  return {
    samples,
    observedReturnPct,
    p5ReturnPct: quantile(results, 0.05),
    medianReturnPct: quantile(results, 0.5),
    p95ReturnPct: quantile(results, 0.95),
    pProfit: profit / results.length,
    distribution: [...results].sort((a, b) => a - b),
  }
}

export interface PermutationDrawdown {
  observedMaxDdPct: number
  medianMaxDdPct: number
  p95MaxDdPct: number
}

// Drawdown depends on the ORDER of returns; shuffling shows how much was luck of sequence.
export function permutationDrawdown(
  stepReturns: readonly number[],
  opts: { samples?: number; seed?: number } = {},
): PermutationDrawdown {
  const samples = opts.samples ?? 500
  const rng = mulberry32(opts.seed ?? 23)
  const observed = maxDdPctOf(stepReturns)
  if (stepReturns.length === 0) return { observedMaxDdPct: 0, medianMaxDdPct: 0, p95MaxDdPct: 0 }

  const dds: number[] = []
  const arr = [...stepReturns]
  for (let s = 0; s < samples; s++) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1))
      const tmp = arr[i]
      arr[i] = arr[j]
      arr[j] = tmp
    }
    dds.push(maxDdPctOf(arr))
  }
  return { observedMaxDdPct: observed, medianMaxDdPct: quantile(dds, 0.5), p95MaxDdPct: quantile(dds, 0.95) }
}

function maxDdPctOf(returns: readonly number[]): number {
  let equity = 1
  const curve: number[] = [equity]
  for (const r of returns) {
    equity *= 1 + r
    curve.push(equity)
  }
  return drawdown(curve).maxDrawdownPct * 100
}

export interface PboResult {
  pbo: number
  evaluations: number
  medianLogit: number
  insufficient: boolean
}

// Probability of Backtest Overfitting via Combinatorially Symmetric Cross-Validation (Bailey et al.).
export function probabilityOfBacktestOverfitting(matrix: readonly number[][]): PboResult {
  const nConfigs = matrix.length
  let segments = nConfigs > 0 ? matrix[0].length : 0
  if (nConfigs < 2 || segments < 4) return { pbo: 0, evaluations: 0, medianLogit: 0, insufficient: true }
  if (segments % 2 === 1) segments -= 1

  const allSegments = Array.from({ length: segments }, (_, i) => i)
  const half = segments / 2
  const logits: number[] = []

  for (const isSet of combinations(allSegments, half)) {
    const oosSet = allSegments.filter((s) => !isSet.includes(s))
    const isPerf = matrix.map((row) => meanOver(row, isSet))
    const oosPerf = matrix.map((row) => meanOver(row, oosSet))

    let bestIs = 0
    for (let c = 1; c < nConfigs; c++) if (isPerf[c] > isPerf[bestIs]) bestIs = c

    const target = oosPerf[bestIs]
    let belowOrEqual = 0
    for (let c = 0; c < nConfigs; c++) if (oosPerf[c] <= target) belowOrEqual++
    const rank = belowOrEqual
    const omega = Math.min(1 - 1e-6, Math.max(1e-6, rank / (nConfigs + 1)))
    logits.push(Math.log(omega / (1 - omega)))
  }

  const negative = logits.filter((l) => l < 0).length
  return {
    pbo: logits.length ? negative / logits.length : 0,
    evaluations: logits.length,
    medianLogit: quantile(logits, 0.5),
    insufficient: false,
  }
}

function meanOver(row: readonly number[], indices: readonly number[]): number {
  if (indices.length === 0) return 0
  let acc = 0
  for (const i of indices) acc += row[i]
  return acc / indices.length
}

function* combinations(items: readonly number[], k: number): Generator<number[]> {
  const n = items.length
  if (k > n) return
  const idx = Array.from({ length: k }, (_, i) => i)
  while (true) {
    yield idx.map((i) => items[i])
    let i = k - 1
    while (i >= 0 && idx[i] === i + n - k) i--
    if (i < 0) return
    idx[i]++
    for (let j = i + 1; j < k; j++) idx[j] = idx[j - 1] + 1
  }
}
